import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button, Checkbox, Divider, Input, Loading, message, Trans } from '@/components'
import { Textarea } from '@/components/base/Textarea'
import { EIPBreadcrumb } from '@/pages/eip/components/EIPBreadcrumb'
import { showCanvasError } from '@/utils/canvasHttp'

import { createModelRoute, fetchRouteForEdit, updateModelRoute } from '../api'
import { ApiKeyModal } from '../components/ApiKeyModal'
import { AUTH_OPTIONS } from '../constants'
import {
  AccessMode,
  CatalogModel,
  ROUTE_TYPE_LABEL_KEYS,
  ROUTE_TYPES,
  RouteModelType,
} from '../types'
import { AccessMethodSection } from './components/AccessMethodSection'
import { ReviewModal } from './components/ReviewModal'
import { RouteTypeSection } from './components/RouteTypeSection'

type SelectionMap = Record<RouteModelType, Map<string, CatalogModel>>

const emptySelections = (): SelectionMap => ({
  MaaS: new Map(),
  Instance: new Map(),
  'Auto Deployment': new Map(),
})

export function NewModelRoutePage() {
  const { t } = useTranslation('modelRoute')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { routeResId } = useParams<{ routeResId: string }>()
  const isEdit = !!routeResId

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [enabledTypes, setEnabledTypes] = useState<RouteModelType[]>(['MaaS'])
  const [selections, setSelections] = useState<SelectionMap>(emptySelections)
  const [auth, setAuth] = useState(AUTH_OPTIONS[0])
  const [accessMode, setAccessMode] = useState<AccessMode>('auto')
  const [reviewOpen, setReviewOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null)

  const { data: editData, isLoading: editLoading } = useQuery({
    queryKey: ['routeEdit', routeResId],
    queryFn: () => fetchRouteForEdit(routeResId as string),
    enabled: isEdit,
  })

  useEffect(() => {
    if (!editData) return
    setName(editData.name)
    setDescription(editData.description)
    // v1 is MaaS-only, so all existing (active) bindings prefill into the MaaS section
    setSelections({
      ...emptySelections(),
      MaaS: new Map(editData.models.map((model) => [model.id, model])),
    })
  }, [editData])

  const toggleType = (type: RouteModelType, checked: boolean) => {
    // 取消勾選只隱藏區塊，已選 models 保留（re-勾選不遺失），payload 時才過濾
    setEnabledTypes((prev) => (checked ? [...prev, type] : prev.filter((t) => t !== type)))
  }

  const toggleModels = (type: RouteModelType) => (models: CatalogModel[], checked: boolean) => {
    setSelections((prev) => {
      const next = new Map(prev[type])
      models.forEach((model) => {
        if (checked) {
          next.set(model.id, model)
        } else {
          next.delete(model.id)
        }
      })
      return { ...prev, [type]: next }
    })
  }

  const orderedEnabledTypes = ROUTE_TYPES.filter((type) => enabledTypes.includes(type))
  const reviewGroups = orderedEnabledTypes
    .map((type) => ({ routeType: type, models: [...selections[type].values()] }))
    .filter((group) => group.models.length > 0)

  const selectedModels = reviewGroups.flatMap((group) => group.models)
  const canConfirm = name.trim().length > 0 && selectedModels.length > 0

  const onCreate = async () => {
    if (creating) return
    setCreating(true)
    try {
      if (isEdit && editData) {
        await updateModelRoute(
          routeResId as string,
          { name: name.trim(), description: description.trim(), models: selectedModels },
          editData,
        )
        message.success(t('create.updated', 'Model route updated'))
        queryClient.invalidateQueries({ queryKey: ['modelRoutes'] })
        queryClient.invalidateQueries({ queryKey: ['modelRouteKpi'] })
        navigate('/model-route')
      } else {
        const { apiKey } = await createModelRoute({
          name: name.trim(),
          description: description.trim(),
          models: selectedModels,
        })
        queryClient.invalidateQueries({ queryKey: ['modelRoutes'] })
        queryClient.invalidateQueries({ queryKey: ['modelRouteKpi'] })
        message.success(t('create.created', 'Model route created'))
        setReviewOpen(false)
        // Show the one-time key before leaving the page — it can never be shown again
        setCreatedApiKey(apiKey)
      }
    } catch (error) {
      showCanvasError(error)
    } finally {
      setCreating(false)
    }
  }

  const pageTitle = isEdit
    ? t('create.editTitle', 'Edit Model Route')
    : t('create.title', 'New Model Route')

  if (isEdit && editLoading) {
    return (
      <div className="card bg-panel-white h-full flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="flex items-center gap-3 h-[60px]">
        <button
          type="button"
          className="text-tertiary cursor-pointer hover:text-primary"
          onClick={() => navigate('/model-route')}
          aria-label="back"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <EIPBreadcrumb
          items={[
            {
              label: (
                <Trans ns="modelRoute" i18nKey="title">
                  Model Route
                </Trans>
              ),
              to: '/model-route',
            },
            {
              label: pageTitle,
              to: isEdit ? `/model-route/${routeResId}/edit` : '/model-route/create',
              active: true,
            },
          ]}
        />
      </div>

      <Divider />

      <div className="flex flex-col gap-8 mt-6 pb-6">
        <div className="flex flex-col gap-2 max-w-[600px]">
          <div className="t-sb text-primary">
            <Trans ns="modelRoute" i18nKey="create.routeName">
              Route Name
            </Trans>
          </div>
          <Input
            className="t-sb"
            placeholder={t('create.routeNamePlaceholder', 'Please Enter Route Name')}
            value={name}
            maxLength={128}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2 max-w-[600px]">
          <div className="t-sb text-primary">
            <Trans ns="modelRoute" i18nKey="create.routeDescription">
              Route Description
            </Trans>
          </div>
          <Textarea
            placeholder={t('create.routeDescriptionPlaceholder', 'Please Enter Route Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="t-sb text-primary">
            <Trans ns="modelRoute" i18nKey="create.routeType">
              Route Type
            </Trans>
          </div>
          <div className="flex gap-4">
            {ROUTE_TYPES.map((type) => {
              // TODO(model-route-api): the catalog is MaaS-only for now — Instance /
              // Auto Deployment stay disabled until they launch.
              const typeAvailable = type === 'MaaS'
              return (
                <div
                  key={type}
                  className="ring-1 ring-neutral-200 rounded-lg px-4 py-3"
                  title={typeAvailable ? undefined : t('comingSoon', 'Coming soon')}
                >
                  <Checkbox
                    checked={enabledTypes.includes(type)}
                    disabled={!typeAvailable}
                    checkedBgColor="bg-success"
                    checkedBorderColor="border-success"
                    onChange={(checked) => toggleType(type, checked)}
                  >
                    <span className={typeAvailable ? 't-sb text-primary' : 't-sb text-tertiary'}>
                      {t(ROUTE_TYPE_LABEL_KEYS[type], type)}
                    </span>
                  </Checkbox>
                </div>
              )
            })}
          </div>
        </div>

        {orderedEnabledTypes.map((type) => (
          <RouteTypeSection
            key={type}
            routeType={type}
            selection={selections[type]}
            onToggle={toggleModels(type)}
          />
        ))}

        <AccessMethodSection
          auth={auth}
          setAuth={setAuth}
          accessMode={accessMode}
          setAccessMode={setAccessMode}
        />

        <div className="flex items-center justify-end gap-4">
          <Button type="outline" className="min-w-[88px]" onClick={() => navigate('/model-route')}>
            <Trans ns="modelRoute" i18nKey="create.cancel">
              Cancel
            </Trans>
          </Button>
          <Button
            type="secondary"
            className="min-w-[88px]"
            disabled={!canConfirm}
            onClick={() => setReviewOpen(true)}
          >
            <Trans ns="modelRoute" i18nKey="create.confirm">
              Confirm
            </Trans>
          </Button>
        </div>
      </div>

      <ReviewModal
        open={reviewOpen}
        groups={reviewGroups}
        creating={creating}
        isEdit={isEdit}
        onClose={() => setReviewOpen(false)}
        onConfirm={onCreate}
      />

      {createdApiKey && (
        <ApiKeyModal
          apiKey={createdApiKey}
          onClose={() => {
            setCreatedApiKey(null)
            navigate('/model-route')
          }}
        />
      )}
    </div>
  )
}
